import ErrorComponent from "@/components/atoms/ErrorComponent";
import Loader from "@/components/atoms/Loader";
import SoftBadge from "@/components/atoms/SoftBadge";
import AreYouSurToContinueModal from "@/components/molecules/modals/AreYouSurToContinueModal";
import StoreEmailTemplateModal from "@/components/molecules/modals/emailTemplates/StoreEmailTemplateModal";
import AllEmailTemplatesHeader from "@/components/molecules/table-headers/AllEmailTemplatesHeader";
import Table from "@/components/molecules/table/Table";
import { getColorFromRole, getReelRoleName } from "@/helpers/UserHelpers";
import { splitText } from "@/helpers/utils";
import EmailTemplate from "@/interfaces/EmailTemplate";
import { useTableState } from "@/hooks/useTableState";
import {
  useDestroyEmailTemplateMutation,
  useGetEmailTemplatesQuery,
} from "@/services/emailTemplates";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnDef, TableState } from "@tanstack/react-table";
import classNames from "classnames";
import { DateTime } from "luxon";
import { ElementRef, FC, Fragment, useMemo, useRef } from "react";
import { Button } from "react-bootstrap";
import { toast } from "react-toastify";

const EmailTemplatesView: FC = () => {
  const DeleteModalRef =
    useRef<ElementRef<typeof AreYouSurToContinueModal>>(null);
  const EditModalRef = useRef<ElementRef<typeof StoreEmailTemplateModal>>(null);

  const [deleteEmailTemplateAsync] = useDestroyEmailTemplateMutation();

  const [state, setState] = useTableState<Partial<TableState>>(
    "email-templates",
    {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      globalFilter: "",
    },
  );

  const { data, isLoading, isError, error } = useGetEmailTemplatesQuery();

  const onDelete = async (id: string) => {
    toast.promise(deleteEmailTemplateAsync(id).unwrap(), {
      pending: "Suppression du template en cours...",
      success: {
        render() {
          DeleteModalRef.current?.showOrHiddenModal();
          return (
            <p className="mb-0 text-center">
              Le template a bien été supprimé 🤩
            </p>
          );
        },
      },
      error: {
        render() {
          return "Une erreur est survenue";
        },
      },
    });
  };

  const onEdit = (id: string) => {
    EditModalRef.current?.showOrHiddenModal(id);
  };

  const columns = useMemo<ColumnDef<EmailTemplate>[]>(
    () => [
      {
        header: "Actions",
        enableSorting: false,
        footer: (props) => props.column.id,
        accessorFn: ({ id }) => (
          <Fragment>
            <Button variant="info" size="sm" onClick={() => onEdit(id)}>
              <FontAwesomeIcon icon={faEdit} />
            </Button>
            <Button
              variant="danger"
              size="sm"
              className="ms-2"
              onClick={() => DeleteModalRef.current?.showOrHiddenModal(id)}
            >
              <FontAwesomeIcon icon={faTrash} />
            </Button>
          </Fragment>
        ),
        id: "actions",
        cell: (info) => info.getValue(),
      },
      {
        header: "#",
        footer: (props) => props.column.id,
        accessorFn: ({ id }) => splitText(id, 10),
        id: "id",
        cell: (info) => info.getValue(),
      },
      {
        header: "Template ID",
        footer: (props) => props.column.id,
        accessorFn: ({ templateId }) => templateId,
        id: "templateId",
        cell: (info) => (
          <code className="text-primary">{info.getValue() as string}</code>
        ),
      },
      {
        header: "Label",
        footer: (props) => props.column.id,
        accessorFn: ({ label }) => label,
        id: "label",
        cell: (info) => info.getValue(),
      },
      {
        header: "Condition",
        footer: (props) => props.column.id,
        accessorFn: ({ condition }) => condition || "Aucune",
        id: "condition",
        cell: (info) => info.getValue(),
      },
      {
        header: "Rôles autorisés",
        footer: (props) => props.column.id,
        enableSorting: false,
        accessorFn: ({ roleEmailTemplates }) => (
          <div>
            {roleEmailTemplates.map((roleTemplate, index) => (
              <SoftBadge
                key={roleTemplate.id}
                bg={getColorFromRole([roleTemplate.role])}
                className={classNames("my-2 text-xl-center", {
                  "ms-1": index !== 0,
                })}
              >
                {getReelRoleName([roleTemplate.role])}
              </SoftBadge>
            ))}
          </div>
        ),
        id: "roles",
        cell: (info) => info.getValue(),
      },
      {
        header: "Date de création",
        footer: (props) => props.column.id,
        accessorFn: ({ createdAt }) =>
          DateTime.fromISO(createdAt).toFormat("dd/MM/yyyy HH:mm"),
        id: "createdAt",
        cell: (info) => info.getValue(),
      },
    ],
    []
  );

  if (isLoading) {
    return <Loader />;
  }

  if (isError) {
    return <ErrorComponent error={error} />;
  }

  return (
    <div className="formalys-page formalys-list-page">
      <AreYouSurToContinueModal
        ref={DeleteModalRef}
        title="Êtes-vous sûr de vouloir supprimer ce template d'email ?"
        onContinue={onDelete}
      />
      <StoreEmailTemplateModal ref={EditModalRef} />
      <Table
        resource="email-templates"
        HeaderComponent={AllEmailTemplatesHeader}
        tableName="email-templates"
        columns={columns}
        state={state}
        onStateChange={setState}
        data={data?.data}
        meta={data?.meta}
        isLoading={isLoading}
      />
    </div>
  );
};

export default EmailTemplatesView;
